import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { Download, FileText, Loader2, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDelete } from "@/components/admin/shared";
import { fmtSize } from "./ProjectBits";

const MAX = 50 * 1024 * 1024;
const TOO_LARGE = "File too large (max 50 MB)";

const download = async (f) => {
  try {
    const { data } = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = f.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch {
    toast.error("Download failed. Please try again.");
  }
};

const FileRow = ({ f, canDelete, onDelete }) => (
  <li data-testid={`file-row-${f.id}`} className="flex flex-col gap-3 border-b border-white/5 p-4 last:border-0 sm:flex-row sm:items-center">
    <button onClick={() => download(f)} data-testid={`file-download-${f.id}`} className="flex min-w-0 flex-1 items-center gap-3 text-left">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-cyan-400/20 bg-cyan-400/5 text-cyan-300"><FileText className="h-4 w-4" /></span>
      <span className="min-w-0">
        <span data-testid={`file-name-${f.id}`} className="block truncate text-sm font-medium text-white hover:text-cyan-200">{f.filename}</span>
        <span className="block text-xs text-slate-500">
          <span data-testid={`file-size-${f.id}`}>{fmtSize(f.size)}</span> · <span data-testid={`file-uploader-${f.id}`}>{f.uploader_name}</span> · {format(new Date(f.created_at), "MMM d, yyyy h:mm a")}
        </span>
      </span>
    </button>
    <div className="flex gap-1 self-end sm:self-auto">
      <button onClick={() => download(f)} aria-label="Download" className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-cyan-300"><Download className="h-4 w-4" /></button>
      {canDelete && <button onClick={() => onDelete(f)} data-testid={`file-delete-${f.id}`} aria-label="Delete" className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>}
    </div>
  </li>
);

export const FilesPanel = ({ projectId }) => {
  const { user } = useAuth();
  const [files, setFiles] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const input = useRef(null);

  const load = () => api.get(`/projects/${projectId}/files`).then((r) => setFiles(r.data)).catch(() => setFiles([]));
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (file) => {
    setError("");
    if (!file) return;
    if (file.size > MAX) { setError(TOO_LARGE); return; }
    const body = new FormData();
    body.append("file", file);
    setProgress(0);
    try {
      await api.post(`/projects/${projectId}/files`, body, { onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)) });
      toast.success(`${file.name} uploaded`);
      load();
    } catch (e) {
      setError(e.response?.status === 413 ? TOO_LARGE : formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setProgress(null);
      if (input.current) input.current.value = "";
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/files/${toDelete.id}`);
      toast.success("File deleted");
      setToDelete(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="files-panel">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-400">Shared files for this project. Max 50 MB per file.</p>
        <input ref={input} type="file" className="hidden" data-testid="file-upload-input" onChange={(e) => upload(e.target.files?.[0])} />
        <button onClick={() => input.current?.click()} disabled={progress !== null} data-testid="file-upload-button"
          className="btn-glow inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-60">
          {progress !== null ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading {progress}%</> : <><UploadCloud className="h-4 w-4" /> Upload file</>}
        </button>
      </div>
      {error && <p data-testid="file-upload-error" role="alert" className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-300">{error}</p>}
      {files === null ? <div className="h-24 animate-pulse rounded-2xl bg-white/[0.03]" /> : files.length === 0 ? (
        <p data-testid="files-empty-state" className="rounded-2xl border border-dashed border-white/10 bg-[#0B0F18] px-6 py-12 text-center text-sm text-slate-500">No files shared yet.</p>
      ) : (
        <ul data-testid="files-list" className="rounded-2xl border border-white/5 bg-[#0B0F18]">
          {files.map((f) => <FileRow key={f.id} f={f} canDelete={user.role === "admin" || f.uploader_id === user.id} onDelete={setToDelete} />)}
        </ul>
      )}
      <ConfirmDelete target={toDelete} label={toDelete?.filename} onCancel={() => setToDelete(null)} onConfirm={remove} />
    </div>
  );
};
